import { alpha, createTheme, darken } from '@mui/material/styles';
import tokens from './theme-tokens.json';

type Mode = 'light' | 'dark';

export const createAppTheme = (mode: Mode) => {
  const t = tokens[mode];
  const hoverOpacity = 0.04;
  const selectedOpacity = 0.04;
  const focusOpacity = 0.04;

  return createTheme({
    palette: {
      mode,
      primary: { main: t.primary, dark: t['primary-hover'], contrastText: t['primary-contrast'] },
      secondary: { main: t.accent, dark: darken(t.accent, 0.08), contrastText: t['accent-contrast'] },
      background: { default: t.bg, paper: t.surface },
      text: { primary: t.text, secondary: t.muted },
      success: { main: t.success },
      warning: { main: t.warning },
      error: { main: t.danger, dark: t['danger-hover'], contrastText: t['danger-contrast'] },
      info: { main: t.info },
      divider: t.border,
      // MUI alpha()/decomposeColor reject CSS color-mix + var(). Guest boot
      // throws minified error #9 on palette.action.hover (DataGrid). Hex + alpha.
      action: {
        hover: alpha(t.muted, hoverOpacity),
        hoverOpacity,
        selected: alpha(t.primary, selectedOpacity),
        selectedOpacity,
        focus: alpha(t.primary, focusOpacity),
        focusOpacity,
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
      fontSize: 14,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: t.bg } },
      },
      MuiButton: {
        defaultProps: { size: 'small', disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            minHeight: 32,
            '@media (max-width: 639px), (pointer: coarse)': { minHeight: 44, minWidth: 44 },
            '&.Mui-focusVisible': { outline: `2px solid ${t['primary-text']}`, outlineOffset: 2 },
            '&:active:not(.Mui-disabled)': { textDecoration: 'underline', textUnderlineOffset: 3 },
            '&.Mui-disabled': { cursor: 'not-allowed' },
          },
          textPrimary: { color: t['primary-text'] },
          outlinedPrimary: { color: t['primary-text'], borderColor: t['primary-text'] },
          textSecondary: { color: t['accent-text'] },
          outlinedSecondary: { color: t['accent-text'], borderColor: t['accent-text'] },
          text: { '&:hover': { backgroundColor: t['surface-2'] } },
          outlined: { '&:hover': { backgroundColor: t['surface-2'] } },
          containedPrimary: { '&:active:not(.Mui-disabled)': { backgroundColor: t['primary-active'] } },
          containedError: { '&:active:not(.Mui-disabled)': { backgroundColor: t['danger-active'] } },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible:not(.Mui-disabled):not(.MuiButton-root)': {
              outline: `2px solid ${t['primary-text']}`,
              outlineOffset: -2,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ ownerState }) => ownerState.color === 'primary' ? {
            '&.Mui-focused:not(.Mui-error):not(.Mui-disabled)': { color: t['primary-text'] },
          } : ownerState.color === 'secondary' ? {
            '&.Mui-focused:not(.Mui-error):not(.Mui-disabled)': { color: t['accent-text'] },
          } : {},
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ ownerState }) => ownerState.color === 'primary' ? { color: t['primary-text'] }
            : ownerState.color === 'secondary' ? { color: t['accent-text'] } : {},
        },
      },
      MuiTab: {
        styleOverrides: {
          textColorPrimary: { '&.Mui-selected': { color: t['primary-text'] } },
          textColorSecondary: { '&.Mui-selected': { color: t['accent-text'] } },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            borderRadius: 6,
            fontSize: '0.875rem',
            minHeight: 32,
            '@media (max-width: 639px), (pointer: coarse)': { minHeight: 44 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t['interactive-boundary'] },
            '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: t['primary-text'] },
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: t['primary-text'] },
          },
          // Multiline renders its own textarea on top of the root, which is the
          // one place a hardcoded background used to leak through as a white
          // slab in dark mode.
          multiline: { backgroundColor: t.surface, padding: 0 },
          input: {
            color: t.text,
            padding: '6px 12px',
            lineHeight: '20px',
            height: '20px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', border: `1px solid ${t.border}` },
        },
      },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 12 } } },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (max-width: 639px), (pointer: coarse)': { minWidth: 44, minHeight: 44 },
          },
          sizeSmall: {
            minWidth: 32, minHeight: 32,
            '@media (max-width: 639px), (pointer: coarse)': { minWidth: 44, minHeight: 44 },
          },
        },
      },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '@media (max-width: 639px), (pointer: coarse)': { minWidth: 44, minHeight: 44 },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            '@media (max-width: 639px), (pointer: coarse)': {
              width: ownerState.size === 'small' ? 60 : 64,
              height: 44,
              padding: ownerState.size === 'small' ? '17px' : '15px',
              overflow: 'visible',
              '& .MuiSwitch-switchBase': {
                width: 44,
                height: 44,
                padding: ownerState.size === 'small' ? 14 : 12,
                '&.Mui-checked': { transform: `translateX(${ownerState.size === 'small' ? 16 : 20}px)` },
                '& .MuiSwitch-input': { left: 0, width: '100%', height: '100%' },
              },
            },
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { backgroundColor: t.surface, border: `1px solid ${t.border}` },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { backgroundColor: t.surface, border: `1px solid ${t.border}` },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&&': { '@media (max-width: 639px), (pointer: coarse)': { minHeight: 44 } },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { backgroundColor: t.surface },
          listbox: { backgroundColor: t.surface },
          option: {
            '&.MuiAutocomplete-option': { '@media (max-width: 639px), (pointer: coarse)': { minHeight: 44 } },
          },
          clearIndicator: {
            minWidth: 32, minHeight: 32,
            '@media (max-width: 639px), (pointer: coarse)': { width: 44, height: 44, minWidth: 44, minHeight: 44, marginRight: 0 },
          },
          popupIndicator: {
            minWidth: 32, minHeight: 32,
            '@media (max-width: 639px), (pointer: coarse)': { width: 44, height: 44, minWidth: 44, minHeight: 44, marginRight: 0 },
          },
          root: {
            '@media (max-width: 639px), (pointer: coarse)': {
              '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': { paddingRight: 53 },
              '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': { paddingRight: 97 },
              '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)', display: 'flex', right: 9 },
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: { backgroundColor: t.surface, border: `1px solid ${t.border}` },
        },
      },
      // @ts-expect-error - MuiDataGrid is from @mui/x-data-grid
      MuiDataGrid: {
        defaultProps: { density: 'standard' },
        styleOverrides: {
          root: {
            '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
              width: 32,
              height: 32,
              '@media (max-width: 639px), (pointer: coarse)': { width: 44, height: 44 },
            },
          },
        },
      },
    },
  });
};
